import React, {useEffect, useId, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

/**
 * El selector de métricas de un gráfico: varias, con tope.
 *
 * Existe porque el select de un solo valor escribía `{metrics: [e.target.value]}` y pisaba el
 * array entero, así que el gráfico de barras aceptaba UNA métrica aunque el modelo, el motor y el
 * validador del backend acepten varias, y la evolución no tenía dónde elegirlas (card PlFYrmTo).
 *
 * No reusa `MultiSelect` ni `MultiSelectAdAccountItem` del Explorador: esos entregan el EVENTO del
 * DOM a su `onChange` y copiar la mitad de ese contrato ya costó un bug en producción en esta
 * misma pantalla (el selector de cuentas de Ajustes, 06/09). Acá `onChange` recibe el array.
 *
 * Dos límites que el control respeta y no inventa:
 *  - el TOPE (`limit`) avisa y no bloquea el guardado: la autoridad es el backend;
 *  - no se puede dejar el gráfico SIN métricas, que es lo único que el backend rechaza de plano
 *    («El gráfico necesita al menos una métrica»). La última tildada queda deshabilitada.
 */
/**
 * Qué métricas quedan al tocar una. `null` = el clic no cambia nada, y es lo que sostiene los dos
 * límites: pasado el tope no se suma, y la última elegida no se puede quitar.
 *
 * Vive afuera del componente y se exporta para poder testearla sola: adentro, el `disabled` del
 * checkbox tapa el guard —jsdom no dispara el clic— y una mutación que borre el guard pasaría en
 * verde. Los dos son necesarios: el `disabled` lo hace ver, esto lo hace cierto.
 */
export function nextSelection(selected, options, metric, limit) {
    const current = selected || [];
    if (current.includes(metric)) {
        if (current.length === 1) return null;
        return current.filter((m) => m !== metric);
    }
    if (current.length >= limit) return null;
    // En el orden del catálogo, no en el del clic: es el orden en que se dibujan las series.
    return (options || []).filter((m) => m === metric || current.includes(m));
}

/**
 * `onAdd` (opcional): al final de la lista, «+ Agregar otra métrica» (C-05), que abre «Métricas y
 * dimensiones». Con el gráfico lleno se ofrece deshabilitado: la métrica nueva no tendría dónde entrar,
 * y el pie ya dice que hay que quitar una.
 */
export function MetricsMultiSelect({value, options, limit, labelOf, onChange, ariaLabel, testId = 'metrics-multiselect',
                                       onAdd = null, addLabel = ''}) {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);
    const buttonRef = useRef(null);
    const menuId = useId();
    const selected = value || [];
    const atLimit = selected.length >= limit;

    useEffect(() => {
        if (!open) return undefined;
        const onClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        // Escape devuelve el foco al botón: sin eso, quien navega con teclado cierra el menú y
        // queda con el foco en la nada, al principio del documento (review C-editor-04).
        const onKeyDown = (e) => {
            if (e.key !== 'Escape') return;
            setOpen(false);
            if (buttonRef.current) buttonRef.current.focus();
        };
        document.addEventListener('mousedown', onClickOutside);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onClickOutside);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    const toggle = (metric) => {
        const next = nextSelection(selected, options, metric, limit);
        if (next) onChange(next);
    };

    const names = selected.map((m) => labelOf(m));
    const summary = names.length
        ? (names.length === 1 ? names[0] : t('informes.panel.metrics_summary', {
            first: names[0], rest: names.length - 1, interpolation: {escapeValue: false},
            defaultValue: '{{first}} +{{rest}}'}))
        : t('informes.panel.pick_metric', 'Métrica…');

    return (
        <div className="inf-metrics-select" ref={wrapperRef} data-testid={testId}>
            {/* `aria-haspopup="true"` y un GRUPO, no una lista de opciones: lo que se abre son
                casillas de verificación reales, y el rol de lista sólo admite opciones — un lector
                de pantalla anunciaba «lista, 0 elementos» y después leía casillas sueltas
                (review C-editor-04). `aria-controls` ata el botón con lo que abre. */}
            <button type="button" className="inf-panel-select inf-metrics-select-button" ref={buttonRef}
                    aria-haspopup="true" aria-expanded={open} aria-controls={open ? menuId : undefined}
                    aria-label={ariaLabel} title={names.join(' · ')}
                    onClick={() => setOpen(!open)}>
                <span className="inf-metrics-select-summary">{summary}</span>
            </button>
            {open && (
                <div className="inf-metrics-menu" role="group" id={menuId} aria-label={ariaLabel}>
                    {/* La lista scrollea; el pie NO. Estando los dos dentro del mismo contenedor con
                        `overflow`, con más de ~8 métricas el pie quedaba 125px por debajo del área
                        visible: el usuario veía ítems en gris y no veía por qué (QA-editor-02). */}
                    <div className="inf-metrics-list">
                    {options.map((metric) => {
                        const checked = selected.includes(metric);
                        const isLastOne = checked && selected.length === 1;
                        const bloqueado = isLastOne || (!checked && atLimit);
                        return (
                            // `-off` acompaña al `:has()` del SCSS, que no está en todos los
                            // navegadores: la señal de «no se puede tocar» no puede depender sólo
                            // de eso (review C-editor-07a).
                            <label key={metric}
                                   className={`inf-metrics-item ${checked ? '-on' : ''} ${bloqueado ? '-off' : ''}`}>
                                <input type="checkbox" checked={checked} disabled={bloqueado}
                                       onChange={() => toggle(metric)}/>
                                <span>{labelOf(metric)}</span>
                            </label>
                        );
                    })}
                    </div>
                    {onAdd && (
                        <button type="button" className="inf-metrics-add" disabled={atLimit}
                                data-testid="metrics-add-more"
                                onClick={() => {
                                    // El foco al botón del selector ANTES de abrir el modal: reactstrap lo
                                    // devuelve ahí al cerrarlo. Si no, quedaba en el `body`, porque este
                                    // botón se desmonta con el menú (review C-rp-fe-aprende-05).
                                    if (buttonRef.current) buttonRef.current.focus();
                                    setOpen(false);
                                    onAdd();
                                }}>
                            {addLabel}
                        </button>
                    )}
                    <div className="inf-metrics-foot">
                        {atLimit
                            ? t('informes.panel.metrics_at_limit', {limit,
                                defaultValue: 'Hasta {{limit}} métricas en este gráfico. Quita una para elegir otra.'})
                            : t('informes.panel.metrics_count', {n: selected.length, limit,
                                defaultValue: '{{n}} de {{limit}} métricas.'})}
                    </div>
                </div>
            )}
        </div>
    );
}
